// Kyte ancillaries-as-step pipeline (capability: ancillariesAsStep).
//
// Flow: holdbook → ancillaries → confirmancillaries → checkout. Three
// lfk_*.php calls, each wrapped in the salabam envelope:
//
//   POST {{kyte.book}}
//     body: { token, offerId, bookFlightRequest: { offerIds, passengers, languageCode } }
//     resp: { id (bookingId), status, ticketStatus, totalCost, currentBalance, … }
//
//   POST {{kyte.ancillaries.shop}}
//     body: { token, contextId, shopAncillariesRequest: { requestedTypes } }
//     resp: { bags?, seatMap?, sportsEquipment?, services?, currency, … }
//
//   POST {{kyte.ancillaries.book}}
//     body: { token, bookingId,
//             bookAncillariesRequest: { passengers: [{ id, ancillaries: [{ id, type, action, quantity, flightSegments }] }] } }
//     resp: { ancillaries, totalCost, currentBalance, ticketStatus, … }
//
// Token + agent are auto-injected by services/commons.js set_axios_payload,
// matching the search / details / bundles wiring.

import Store from '@/vuex/store';
import { service_handler } from '@/services';
import { log } from '@/utilities/commons';
import {
  minor_to_decimal,
  as_number,
  make_price,
} from '@liveflight/providers/base/helpers';
import { build_extra_bags_catalog } from './bags';
import { build_seatmap_envelope } from './seats';
import { ensure_infant_associations, unwrap } from './helpers';
import { normalize_booking, build_passengers_for_details } from './normalize';
import {
  KYTE_TYPE_ICONS,
  KYTE_ANCILLARY_CAPABILITIES,
  get_kyte_ancillary_info,
  TITLE_BY_GENDER,
  OFFER_TYPE_FROM_KYTE,
  is_bundle_service,
  parse_bag_axes,
} from './resources/ancillaries';
import {
  build_retrieve_price,
  extract_committed_ancillaries,
} from './pricing-breakdown';

/**
 * Walk a BookFlight response for the hold-expiry timestamp. Kyte
 * surfaces it as `ticketTimeLimit` on either the envelope or the
 * nested booking — recursion handles both.
 *
 * @param {object} response
 * @returns {string} ISO timestamp, or "" when absent
 */
const extract_expires_at = (response) => {
  if (!response) {
    return '';
  }
  if (response.ticketTimeLimit) {
    return response.ticketTimeLimit;
  }
  if (response.booking) {
    return extract_expires_at(response.booking);
  }
  return '';
};

const build_phone_array = (contact_details) => {
  const safe = contact_details || {};
  const number = (safe.phone || '').toString();
  if (!number) return [];
  const country_calling_code = (safe.country_calling_code || '').toString();
  return [
    {
      countryCode: country_calling_code
        ? `+${country_calling_code.replace(/^\+/, '')}`
        : '',
      number,
      type: 'Mobile',
    },
  ];
};

const build_contact_information = (contact_details) => {
  const safe = contact_details || {};
  return {
    email: safe.email || '',
    phone: build_phone_array(safe),
  };
};

/**
 * Build the BookFlight passengers[] payload, pairing travelerPricings to
 * traveler_details by `detail.id` (which mirrors travelerId). Falls
 * back to positional index when the lookup misses — protects against
 * PNR mis-attribution after CHILD/HELD_INFANT reorderings without
 * breaking on a malformed form.
 *
 * @param {object} flight_offer canonical FlightOffer
 * @param {Array<object>} traveler_details form data
 * @param {object} contact_details
 * @returns {Array<object>} BookFlight passengers[]
 */
const build_held_passengers = (
  flight_offer,
  traveler_details,
  contact_details
) => {
  const { travelerPricings = [] } = flight_offer || {};
  const details = Array.isArray(traveler_details) ? traveler_details : [];
  const detail_by_id = {};
  details.forEach((d) => {
    if (d && d.id) detail_by_id[d.id] = d;
  });
  const contact_info = build_contact_information(contact_details);
  return travelerPricings
    .filter((tp) => tp && tp.travelerId)
    .map((tp, index) => {
      const detail = detail_by_id[tp.travelerId] || details[index] || {};
      const gender = (detail.gender || 'MALE').toUpperCase();
      return {
        id: tp.travelerId,
        title: TITLE_BY_GENDER[gender] || 'mr',
        firstName: detail.firstname || '',
        lastName: detail.lastname || '',
        dateOfBirth: detail.birth || '',
        gender: gender.toLowerCase(),
        contactInformation: contact_info,
      };
    });
};

/**
 * Build the FlightPrice.additionalServices roll-up. Sums pricingBreakdown
 * by canonical type and attaches per-type `info` (the resume sidebar
 * branches on `type` to render). Without this the post-book sidebar
 * falls back to pre-book flexible_bags Vuex state.
 *
 * `info` shape:
 *   - bag-shaped (CHECKED_BAGS / SPORTS_EQUIPMENT / SERVICES): dict by
 *     description, each `{ description, icon, content }`
 *   - SEATS: dict by segmentId, each `{ seatNumber, travelerId, traveler_name }[]`
 *
 * @param {object} booking
 * @param {number} decimals
 * @param {string} currencyCode
 * @param {Array<object>} committed output of extract_committed_ancillaries
 * @returns {Array<object>} canonical AdditionalService[]
 */
const build_additional_services = (
  booking,
  decimals,
  currencyCode,
  committed
) => {
  const { pricingBreakdown: breakdown = [] } = booking || {};
  const totals = {};
  breakdown.forEach((row) => {
    const {
      category = '',
      type: kyte_type = '',
      id: row_id = '',
      price = 0,
    } = row || {};
    if (category !== 'ancillary') {
      return;
    }
    if (is_bundle_service(kyte_type, row_id)) {
      return;
    }
    const canonical_type = OFFER_TYPE_FROM_KYTE[kyte_type];
    if (!canonical_type) {
      return;
    }
    const price_minor = as_number(price);
    if (Number.isNaN(price_minor)) {
      return;
    }
    totals[canonical_type] = (totals[canonical_type] || 0) + price_minor;
  });

  const info_by_type = {};
  (Array.isArray(committed) ? committed : []).forEach((entry) => {
    const {
      type: kyte_type = '',
      id = '',
      flightSegments = [],
      travelerId = '',
      quantity,
    } = entry || {};
    if (is_bundle_service(kyte_type, id)) return;
    const canonical_type = OFFER_TYPE_FROM_KYTE[kyte_type];
    if (!canonical_type) return;
    if (!info_by_type[canonical_type]) info_by_type[canonical_type] = {};
    const bucket = info_by_type[canonical_type];

    if (canonical_type === 'SEATS') {
      if (!id) return;
      flightSegments.forEach((segment_id) => {
        if (!bucket[segment_id]) bucket[segment_id] = [];
        bucket[segment_id].push({
          seatNumber: id,
          travelerId,
          traveler_name: '',
        });
      });
      return;
    }

    // Localize the upstream code via the provider resource; bucket-by-id
    // collapses identical entries across segments into a single count.
    const info = get_kyte_ancillary_info(entry, kyte_type);
    const description = info.txt || id;
    if (!description) return;
    const bucket_key = id || description;
    const qty = Number.isFinite(as_number(quantity))
      ? as_number(quantity) || 1
      : 1;
    if (!bucket[bucket_key]) {
      bucket[bucket_key] = {
        description,
        tooltip: info.tooltip || '',
        icon: info.icon || KYTE_TYPE_ICONS[canonical_type] || 'check_circle',
        content: 0,
      };
    }
    bucket[bucket_key].content += qty;
  });

  // Union breakdown totals with committed info so fare-included types
  // (e.g. SEATS on a Regular fare, no pricingBreakdown row) still emit
  // a SEATS section with the booked seat list.
  const types = new Set([...Object.keys(totals), ...Object.keys(info_by_type)]);
  return Array.from(types).map((type) => {
    const entry = {
      type,
      price: make_price(
        minor_to_decimal(totals[type] || 0, decimals),
        currencyCode
      ),
    };
    if (info_by_type[type] && Object.keys(info_by_type[type]).length) {
      entry.info = info_by_type[type];
    }
    return entry;
  });
};

/**
 * Build a FareDetailAdditionalService entry from a committed ancillary.
 * Seat code lives in `seatNumber` (canonical); other types carry `id`
 * + `quantity` at the root. The original Kyte object is kept on `_raw`
 * for provider-specific reads.
 *
 * @param {object} committed_entry
 * @returns {object|null} canonical FareDetailAdditionalService, or null when type is missing
 */
const build_fare_detail_entry = (committed_entry) => {
  const {
    type = '',
    id = '',
    quantity = undefined,
    flightSegments = [],
    price = null,
    included = false,
  } = committed_entry || {};
  if (!type) return null;
  const entry = { type, _raw: committed_entry };
  if (price) entry.price = price;
  if (included) entry.included = true;
  if (type === 'seat') {
    entry.seatNumber = id;
    return entry;
  }
  if (id) entry.id = id;
  if (quantity !== undefined) entry.quantity = as_number(quantity);
  const info = get_kyte_ancillary_info(committed_entry, type);
  if (info.txt) entry.name = info.txt;
  if (info.tooltip) entry.tooltip = info.tooltip;
  if (info.icon) entry.icon = info.icon;
  if (flightSegments.length) entry.flightSegments = [...flightSegments];
  return entry;
};

/**
 * Drop bag-type committed entries that are already covered by the
 * fare's includedCheckedBags allowance. Some LCC carriers (Ryanair,
 * Wizz on certain fares) ship the same physical bag both as a fare-
 * included allowance AND as a separate zero-priced ancillary echo, which
 * would otherwise show up twice in the resume row + post-book breakdown.
 *
 * Only the free echo is de-duplicated: a bag that matched a
 * pricingBreakdown ancillary row (extract_committed_ancillaries →
 * `included: false`, real price) is a genuine paid extra and is NEVER a
 * duplicate of the fare-included allowance — it is always kept, even on
 * a fare that bundles a checked bag. Without this a Wizz PLUS booking
 * (1 bag bundled) that buys extra bags rendered no bags at all: the
 * whole quantity-N paid entry was dropped against the single free
 * counter.
 *
 * Among free echoes, an entry is dropped only when EVERY segment it
 * covers still has a remaining included-bag counter for that traveler AND
 * the committed bag is no heavier than the included allowance; counters
 * are decremented in lockstep so multi-leg coverage is consistent. Only a
 * genuine weight upgrade (heavier than the fare allowance, e.g. a 26kg bag
 * on a 20kg-included fare) is kept; a same/lighter free echo is the
 * included bag itself and must not surface as a purchased extra. Partial
 * overlaps also keep the entry.
 *
 * @param {Array<object>} committed
 * @param {Array<object>} travelerPricings post-normalize, pre-stamp
 * @returns {Array<object>} filtered committed list
 */
const drop_committed_covered_by_included = (committed, travelerPricings) => {
  const list = Array.isArray(committed) ? committed : [];
  if (!list.length) return list;
  const counters = {};
  const weights = {};
  let any = false;
  const tps = Array.isArray(travelerPricings) ? travelerPricings : [];
  tps.forEach((tp) => {
    const { travelerId = '', fareDetailsBySegment = [] } = tp || {};
    const fdbs = Array.isArray(fareDetailsBySegment)
      ? fareDetailsBySegment
      : [];
    fdbs.forEach((fd) => {
      const { segmentId = '', includedCheckedBags = {} } = fd || {};
      const icb = includedCheckedBags || {};
      const n = as_number(icb.quantity);
      if (n > 0 && travelerId && segmentId) {
        const key = `${travelerId}:${segmentId}`;
        counters[key] = n;
        weights[key] = as_number(icb.weight) || 0;
        any = true;
      }
    });
  });
  if (!any) return list;
  return list.filter((entry) => {
    const {
      type = '',
      travelerId = '',
      flightSegments = [],
      included = false,
    } = entry || {};
    if (type !== 'bag') return true;
    // Paid extra (separately charged) — keep regardless of the fare's
    // included allowance; it isn't a duplicate of the free bag.
    if (!included) return true;
    const segments = Array.isArray(flightSegments) ? flightSegments : [];
    if (!segments.length || !travelerId) return true;
    const covered = segments.every(
      (seg) => (counters[`${travelerId}:${seg}`] || 0) > 0
    );
    if (!covered) return true;
    // Only a bag heavier than the allowance is a weight upgrade worth
    // keeping; a same/lighter free echo is the included bag and is
    // dropped. When either weight is unknown, fall back to count-only.
    const entry_kg = parse_bag_axes(entry.id).weight_kg;
    const covered_by_weight = segments.every((seg) => {
      const inc_kg = weights[`${travelerId}:${seg}`] || 0;
      return !entry_kg || !inc_kg ? true : entry_kg <= inc_kg;
    });
    if (!covered_by_weight) return true;
    segments.forEach((seg) => {
      counters[`${travelerId}:${seg}`] -= 1;
    });
    return false;
  });
};

// Wizzair-style carriers encode checked bags as `checked-in-bag-NN` and report
// the committed `quantity` as the TOTAL pieces at that weight — INCLUSIVE of the
// fare-included bag. Fixed-code carriers (Ryanair BBG/CBAG, EasyJet LUG/LUS) are
// additive, so reconcile_inclusive_bags is scoped to this id shape and leaves
// them (and Ryanair's free BUGY infant item) untouched.
const INCLUSIVE_CHECKED_BAG_RE = /checked-in-bag-\d+/i;

const is_inclusive_checked_bag = (entry) => {
  const { type = '', id = '' } = entry || {};
  return type === 'bag' && INCLUSIVE_CHECKED_BAG_RE.test(String(id));
};

/**
 * Reconcile the fare's includedCheckedBags allowance against Wizzair's
 * inclusive committed checked-bag lines so the same physical bag isn't rendered
 * both as "included" AND as an "extra". A GO fare bundles a 20kg bag; buying one
 * more surfaces a committed `checked-in-bag-20` quantity=2 (verified live), of
 * which 1 is the bundled bag — while includedCheckedBags separately claims it.
 *
 * Per (traveler, segment) the committed count is the source of truth for the
 * physical total; the allowance is only a free/paid label:
 *   - a committed line whose weight MATCHES the included weight carries the fare
 *     bag → subtract the allowance count from that line's quantity
 *     (extra = count − included), keeping includedCheckedBags as the free bag.
 *   - no matching-weight line (a heavier/lighter replacement — Wizz's own copy
 *     reads "Replaces the included 20kg bag") → zero includedCheckedBags for
 *     that cell; every committed line stays a paid extra.
 *
 * Only `checked-in-bag-NN` lines participate; fixed-code carriers keep today's
 * additive rendering. Prices are never altered — price.additionalServices still
 * sums pricingBreakdown, so the money is unaffected by these display counts.
 *
 * @param {Array<object>} committed free-echo-filtered committed list
 * @param {Array<object>} travelerPricings
 * @returns {{committed: Array<object>, travelerPricings: Array<object>}}
 */
const reconcile_inclusive_bags = (committed, traveler_pricings) => {
  const list = Array.isArray(committed) ? committed : [];
  const pricings = Array.isArray(traveler_pricings) ? traveler_pricings : [];

  const included = {};
  pricings.forEach((pricing) => {
    const { travelerId = '', fareDetailsBySegment = [] } = pricing || {};
    const fare_details = Array.isArray(fareDetailsBySegment)
      ? fareDetailsBySegment
      : [];
    fare_details.forEach((fare_detail) => {
      const { segmentId = '', includedCheckedBags = {} } = fare_detail || {};
      const allowance = includedCheckedBags || {};
      const qty = as_number(allowance.quantity);
      if (qty > 0 && travelerId && segmentId) {
        included[`${travelerId}:${segmentId}`] = {
          qty,
          weight_kg: as_number(allowance.weight) || 0,
        };
      }
    });
  });
  if (!Object.keys(included).length) {
    return { committed: list, travelerPricings: pricings };
  }

  const weight_matches = (entry_kg, inc_kg) =>
    !entry_kg || !inc_kg ? true : entry_kg === inc_kg;

  // Which (traveler, segment) cells are covered by an inclusive line, and which
  // of those have a weight-matching line (the fare bag is one of the pieces).
  const has_inclusive_cell = {};
  const matched_cell = {};
  list.forEach((entry) => {
    if (!is_inclusive_checked_bag(entry)) return;
    const { travelerId = '', flightSegments = [] } = entry || {};
    const segments = Array.isArray(flightSegments) ? flightSegments : [];
    const entry_kg = parse_bag_axes(entry.id).weight_kg;
    segments.forEach((seg) => {
      const key = `${travelerId}:${seg}`;
      const inc = included[key];
      if (!inc) return;
      has_inclusive_cell[key] = true;
      if (weight_matches(entry_kg, inc.weight_kg)) matched_cell[key] = true;
    });
  });

  // Match case: consume the allowance out of matching lines in lockstep so two
  // same-weight lines in one cell don't each subtract the single free bag.
  const remaining = {};
  Object.keys(matched_cell).forEach((key) => {
    remaining[key] = included[key].qty;
  });
  const next_committed = list
    .map((entry) => {
      if (!is_inclusive_checked_bag(entry)) return entry;
      const { travelerId = '', flightSegments = [], quantity } = entry || {};
      const segments = Array.isArray(flightSegments) ? flightSegments : [];
      const entry_kg = parse_bag_axes(entry.id).weight_kg;
      let qty = as_number(quantity);
      segments.forEach((seg) => {
        const key = `${travelerId}:${seg}`;
        if (!matched_cell[key]) return;
        if (!weight_matches(entry_kg, included[key].weight_kg)) return;
        const take = Math.min(remaining[key] || 0, qty);
        qty -= take;
        remaining[key] -= take;
      });
      if (qty === as_number(quantity)) return entry;
      return { ...entry, quantity: qty };
    })
    .filter((entry) => {
      if (!is_inclusive_checked_bag(entry)) return true;
      return as_number(entry.quantity) > 0;
    });

  // Replace case: an inclusive line covers the cell but none matched the
  // included weight → the fare bag was upgraded away; drop the allowance.
  const next_pricings = pricings.map((pricing) => {
    const { travelerId = '', fareDetailsBySegment = [] } = pricing || {};
    const fare_details = Array.isArray(fareDetailsBySegment)
      ? fareDetailsBySegment
      : [];
    const next_details = fare_details.map((fare_detail) => {
      const { segmentId = '' } = fare_detail || {};
      const key = `${travelerId}:${segmentId}`;
      if (has_inclusive_cell[key] && !matched_cell[key]) {
        return { ...fare_detail, includedCheckedBags: { quantity: 0 } };
      }
      return fare_detail;
    });
    return { ...pricing, fareDetailsBySegment: next_details };
  });

  return { committed: next_committed, travelerPricings: next_pricings };
};

const group_committed_by_pax_segment = (committed) => {
  const list = Array.isArray(committed) ? committed : [];
  const by_pax_segment = {};
  list.forEach((entry) => {
    const { travelerId = '', flightSegments = [] } = entry || {};
    if (!travelerId) return;
    const segments = Array.isArray(flightSegments) ? flightSegments : [];
    if (!segments.length) return;
    const built = build_fare_detail_entry(entry);
    if (!built) return;
    segments.forEach((segId) => {
      const key = `${travelerId}:${segId}`;
      if (!by_pax_segment[key]) by_pax_segment[key] = [];
      by_pax_segment[key].push(built);
    });
  });
  return by_pax_segment;
};

/**
 * Stamp per-(pax, segment) entries onto
 * fareDetailsBySegment[].additionalServices. Cells without committed
 * ancillaries keep their existing value.
 *
 * @param {Array<object>} travelerPricings
 * @param {Object<string, object[]>} by_pax_segment keyed by `travelerId:segmentId`
 * @returns {Array<object>} new travelerPricings with stamped fareDetailsBySegment
 */
const stamp_additional_services = (travelerPricings, by_pax_segment) => {
  const list = Array.isArray(travelerPricings) ? travelerPricings : [];
  if (!Object.keys(by_pax_segment).length) return list;
  return list.map((tp) => {
    const { travelerId = '', fareDetailsBySegment = [] } = tp || {};
    if (!travelerId) return tp;
    const next_details = fareDetailsBySegment.map((fd) => {
      const { segmentId = '' } = fd || {};
      if (!segmentId) return fd;
      const entries = by_pax_segment[`${travelerId}:${segmentId}`] || [];
      if (!entries.length) return fd;
      return { ...fd, additionalServices: entries };
    });
    return { ...tp, fareDetailsBySegment: next_details };
  });
};

/**
 * Project a Kyte booking (Retrieve or Confirm — same shape) onto the
 * canonical FlightOffer:
 *  - offer-level price (total/grandTotal from totalCost, additionalServices roll-up)
 *  - per-(pax, segment) fareDetailsBySegment[].additionalServices
 *  - infant-association synthesis (Kyte never echoes associatedAdultId)
 *
 * Per-entry prices on FareDetailAdditionalService are best-effort:
 * confirm uses synthetic ids (SEAT-1…) so the (pax,type,id) lookup
 * misses; the offer-level rollup is the source of truth either way.
 *
 * @param {object} flight_offer canonical FlightOffer (search/details time)
 * @param {object} booking RetrieveBooking or ConfirmBooking response
 * @returns {object} folded FlightOffer
 */
export const fold_committed_ancillaries = (flight_offer, booking) => {
  if (!flight_offer) return flight_offer;
  const { currency = {} } = booking || {};
  const { decimals = 2, code: currencyCode = 'EUR' } = currency;
  const committed_raw = extract_committed_ancillaries(
    booking,
    decimals,
    currencyCode
  );
  const price_partial = build_retrieve_price(booking) || {};
  const { travelerPricings = [], price: offer_price = {} } = flight_offer;
  // Drop free-echo bag entries already covered by the fare's
  // includedCheckedBags so the same physical bag isn't counted twice
  // (LCC carriers ship it both ways on some fares).
  const deduped = drop_committed_covered_by_included(
    committed_raw,
    travelerPricings
  );
  // Reconcile Wizzair's INCLUSIVE committed checked-bag counts (a paid extra
  // whose quantity already contains the fare bag) against includedCheckedBags,
  // adjusting display counts only. Fixed-code carriers (Ryanair) are untouched.
  const { committed, travelerPricings: reconciled_traveler_pricings } =
    reconcile_inclusive_bags(deduped, travelerPricings);
  const by_pax_segment = group_committed_by_pax_segment(committed);
  const stamped = stamp_additional_services(
    reconciled_traveler_pricings,
    by_pax_segment
  );
  return {
    ...flight_offer,
    price: {
      ...offer_price,
      ...price_partial,
      additionalServices: build_additional_services(
        booking,
        decimals,
        currencyCode,
        committed
      ),
    },
    travelerPricings: ensure_infant_associations(stamped),
  };
};

/**
 * Fetch /details for an offerId so retrieve() can fold structured
 * baggageAllowance into the re-normalized offer (retrieve itself doesn't
 * ship it). Returns [] on any failure so the parallel retrieve isn't
 * blocked — fare-description tokenizer still produces bag amenity rows.
 *
 * @param {string} existing_offer_id
 * @param {object} flight_offer
 * @returns {Promise<Array>} baggageAllowance entries, or []
 */
const fetch_baggage_allowance = (existing_offer_id, flight_offer) => {
  if (!existing_offer_id) return Promise.resolve([]);
  const body = {
    offerDetailsRequest: {
      offerIds: [existing_offer_id],
      passengers: build_passengers_for_details(flight_offer || {}),
    },
  };
  return service_handler('kyte.details', body, { modal: false })
    .then((response) => {
      const envelope = unwrap(response) || {};
      const { details = {} } = envelope;
      const entry = details[existing_offer_id];
      const { baggageAllowance = [] } = entry || {};
      return baggageAllowance;
    })
    .catch((e) => {
      log(
        '[kyte/retrieve] /details enrichment failed, continuing without baggageAllowance',
        e
      );
      return [];
    });
};

/**
 * Fetch a Kyte booking + offer details in parallel and project them
 * onto the canonical FlightOffer. Falls back to the FE-stored offer
 * if normalize_booking can't satisfy its preconditions.
 *
 * @param {object} provider KyteProvider instance
 * @param {object} flight_offer FE-stored canonical FlightOffer
 * @param {string} booking_id
 * @returns {Promise<{booking, flight_offer, ancillaries, expiresAt}>}
 */
export const retrieve = (provider, flight_offer, booking_id) => {
  const safe_offer = flight_offer || {};
  const existing_offer_id = safe_offer.providerOfferId || safe_offer.id || '';

  return Promise.all([
    service_handler('kyte.retrieve', { bookingId: booking_id || '' }),
    fetch_baggage_allowance(existing_offer_id, flight_offer),
  ]).then(([retrieve_response, baggage_allowance]) => {
    const envelope = unwrap(retrieve_response);
    const { booking = null } = envelope || {};
    const { currency = {} } = booking || {};
    const { decimals = 2, code = 'EUR' } = currency;
    const ancillaries = extract_committed_ancillaries(booking, decimals, code);

    // normalize_booking owns the contract that the refreshed offer
    // reflects the held booking. If it throws we MUST NOT fall back to
    // the FE offer + fold_committed_ancillaries: that would render
    // pre-hold fares while pretending the ancillaries on top are
    // already booked. Signal failure to the caller via flight_offer:null
    // so the page reads the existing store offer (and only the catalog
    // from the envelope is consumed).
    let refreshed = null;
    if (booking) {
      try {
        refreshed = normalize_booking(provider, booking, {
          existingOfferId: existing_offer_id,
          baggageAllowance: baggage_allowance,
        });
      } catch (e) {
        log('[kyte/retrieve] normalize_booking failed', e);
      }
    }

    return {
      booking,
      flight_offer: refreshed
        ? fold_committed_ancillaries(refreshed, booking)
        : null,
      ancillaries,
      expiresAt: extract_expires_at(envelope),
    };
  });
};

/**
 * Dev-only: persist Kyte hold-expiry as `checkout_timer_*` slices so
 * resume.vue can render a countdown across reloads (selection.js opts
 * both keys into localStorage). No-op outside `development` mode.
 *
 * @param {string} expires_at_iso
 */
const write_checkout_timer = (expires_at_iso) => {
  const expires_ms = Date.parse(expires_at_iso || '');
  if (!expires_ms) return;
  const now = Date.now();
  const remaining = Math.max(0, Math.floor((expires_ms - now) / 1000));
  Store.commit('liveflight/checkout_timer_start', now);
  Store.commit('liveflight/checkout_timer_amount', remaining);
};

export const book_held = (
  _provider,
  flight_offer,
  traveler_details,
  contact_details
) => {
  const { providerOfferId = '' } = flight_offer || {};
  const body = {
    offerId: providerOfferId,
    bookFlightRequest: {
      offerIds: providerOfferId ? [providerOfferId] : [],
      passengers: build_held_passengers(
        flight_offer,
        traveler_details,
        contact_details
      ),
      languageCode: 'it-IT',
    },
  };
  return service_handler('kyte.book', body).then((response) => {
    const envelope = unwrap(response);
    const { id = '' } = envelope || {};
    const expires_at = extract_expires_at(envelope);
    write_checkout_timer(expires_at);
    return {
      bookingId: id,
      expiresAt: expires_at,
      flight_offer,
    };
  });
};

/**
 * Normalize the unified ShopAncillaries response. Each row of
 * KYTE_ANCILLARY_CAPABILITIES is built only when the provider advertises
 * its capability, so a disabled type never surfaces on the envelope (the
 * /ancillaries page hides steps whose slice is absent). The bag, service,
 * sportsEquipment, meal, loungeAccess and disabilityAssistance slices share
 * an entry shape so build_extra_bags_catalog handles them all; seat goes
 * through build_seatmap_envelope.
 *
 * @param {object} envelope kyte.ancillaries.shop response body
 * @param {object} flight_offer
 * @param {object} provider
 * @returns {object} canonical envelope keyed by the enabled `envelopeKey`s + currency
 */
const normalize_ancillaries_envelope = (envelope, flight_offer, provider) => {
  const safe = envelope || {};
  const { currency } = safe;
  const out = {
    currency,
    actionList: safe.actionList,
    possibleActions: safe.possibleActions,
  };
  KYTE_ANCILLARY_CAPABILITIES.forEach((entry) => {
    if (!provider || !provider.has_capability(entry.capability)) {
      return;
    }
    if (entry.catalogType) {
      out[entry.envelopeKey] = build_extra_bags_catalog(
        safe[entry.sourceKey],
        flight_offer,
        provider,
        currency,
        entry.catalogType
      );
      return;
    }
    out[entry.envelopeKey] = build_seatmap_envelope(
      safe[entry.sourceKey],
      flight_offer,
      currency
    );
  });
  return out;
};

export const fetch_ancillaries = (provider, flight_offer, booking_id) => {
  const requestedTypes = KYTE_ANCILLARY_CAPABILITIES.filter(
    (entry) => provider && provider.has_capability(entry.capability)
  ).map((entry) => entry.request);
  if (!requestedTypes.length) {
    return Promise.resolve({});
  }
  const body = {
    contextId: booking_id || '',
    shopAncillariesRequest: { requestedTypes },
  };
  return service_handler('kyte.ancillaries.shop', body, { modal: false }).then(
    (response) =>
      normalize_ancillaries_envelope(unwrap(response), flight_offer, provider)
  );
};

/**
 * Push a canonical ancillary entry onto the per-traveler bucket with
 * the requested action. Both sides of the diff use the same shape
 * (extract_committed_ancillaries on retrieve, flatten_selections on
 * the page).
 *
 * @param {Object<string, object[]>} by_traveler in/out
 * @param {object} entry `{travelerId, type, id, quantity, flightSegments}`
 * @param {'add'|'remove'} action
 */
const push_ancillary = (by_traveler, entry, action) => {
  const {
    travelerId = '',
    type = '',
    id = '',
    quantity = 1,
    flightSegments = [],
  } = entry || {};
  if (!travelerId || !type || !id) return;
  if (!by_traveler[travelerId]) by_traveler[travelerId] = [];
  by_traveler[travelerId].push({
    id,
    type,
    action,
    quantity: Number(quantity) || 1,
    flightSegments: Array.isArray(flightSegments) ? [...flightSegments] : [],
  });
};

/**
 * Translate a diff into Kyte's BookAncillaries payload. Each `to_add`
 * entry becomes an `action:'add'` row, each `to_remove` an
 * `action:'remove'` — so toggling a previously-booked item off on a
 * re-entry correctly removes it instead of double-adding.
 *
 * @param {{to_add?: object[], to_remove?: object[]}} diff entries shaped
 *   `{travelerId, type, id, quantity, flightSegments}`
 * @returns {{passengers: object[]}}
 */
export const build_book_ancillaries_request = (diff) => {
  const { to_add = [], to_remove = [] } = diff || {};
  const by_traveler = {};
  to_add.forEach((entry) => push_ancillary(by_traveler, entry, 'add'));
  to_remove.forEach((entry) => push_ancillary(by_traveler, entry, 'remove'));
  return {
    passengers: Object.keys(by_traveler).map((traveler_id) => ({
      id: traveler_id,
      ancillaries: by_traveler[traveler_id],
    })),
  };
};

/**
 * Submit the ancillary diff to Kyte. The response does NOT carry a
 * repriced offer — callers must follow with retrieve() to refresh
 * totals. flight_offer is passed through unchanged so the
 * `{flight_offer, expiresAt}` contract stays compatible with the base
 * dispatcher.
 *
 * @param {object} _provider unused (signature parity with base dispatcher)
 * @param {object} flight_offer
 * @param {string} booking_id
 * @param {object} diff `{to_add, to_remove}`
 * @returns {Promise<{flight_offer, expiresAt}>}
 */
export const book_ancillaries = (_provider, flight_offer, booking_id, diff) => {
  const body = {
    bookingId: booking_id || '',
    bookAncillariesRequest: build_book_ancillaries_request(diff),
  };
  return service_handler('kyte.ancillaries.book', body).then((response) => {
    const envelope = unwrap(response);
    const expires_at = extract_expires_at(envelope);
    write_checkout_timer(expires_at);
    return {
      flight_offer,
      expiresAt: expires_at,
    };
  });
};
